import { defineComponent as _defineComponent } from 'vue'
import CountrySwitcherModal from '@/features/buybox/components/CountrySwitcherModal.vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { BuyboxPresentationType, useUserStore } from '@/stores/pinia/useUserStore';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { PresentationType, MonetizationType } from '@/@types/graphql-types';
import { PresentationTypes } from '@/interfaces/titles';
import ChipButton from '@/ui-components/ChipButton.vue';
import ChipButtons from '@/ui-components/ChipButtons.vue';
import { useRoute } from 'vue-router/composables';
import { computed, onMounted, ref } from 'vue';
import { faAngleDown, faFilter } from '@fortawesome/free-solid-svg-icons';
// EXP_PROVIDER_BUYBOX_V2
import MyServicesChip from '@/components/experiments/ProviderBuyboxExp/components/MyServicesChip.vue';
import { useProviderBuyboxFilter } from '@/components/experiments/ProviderBuyboxExp/composables/useProviderBuyboxFilter';
// EXP_PROVIDER_BUYBOX_V2
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'GridBuyBoxFilter',
  props: {
    isInModal: { type: Boolean },
    defaultPresentationType: { default: null },
    additionalContexts: { default: () => [] },
    isEpisodeBuybox: { type: Boolean },
    episodeBuyBoxPresentationType: { default: null },
    titleId: null
  },
  emits: ["episodeFilterChange"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'episodeFilterChange', presentationType: BuyboxPresentationType | null): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { isInModal?: boolean, defaultPresentationType: BuyboxPresentationType | null, additionalContexts: SnowplowContext[], isEpisodeBuybox?: boolean, episodeBuyBoxPresentationType: BuyboxPresentationType | null, titleId?: string };




const languageStore = useLanguageStore();
const userStore = useUserStore();

const route = useRoute();

const { tc } = useI18n();
const filterOptions = computed(() => [
	{
		label: tc('WEBAPP_QUALITY_BESTPRICE'),
		value: null,
		isQuality: false,
	},
	{
		label: tc('WEBAPP_BUYBOX_FREE_FILTER'),
		value: MonetizationType.Free,
		isQuality: false,
	} as const,
	...PresentationTypes.filter(g => g.isQuality),
]);

function openCountrySwitcherModal() {
	ModalHelper.openModal(CountrySwitcherModal, { closable: true });
	TrackingHelper.trackEvent('userinteraction', { action: 'country_selector_clicked' }, props.additionalContexts);
}

// EXP_PROVIDER_BUYBOX_V2
const { providerBuyboxExpVariant, deactivateMyServices, myServicesChip, myServicesActive, handleMyServicesChipClick } =
	useProviderBuyboxFilter({ titleId: () => props.titleId });
// EXP_PROVIDER_BUYBOX_V2

// EXP_PROVIDER_BUYBOX_V2: variant_1 and variant_3 put Free before My Services chip
const myServicesInsertIndex = computed(() =>
	['variant_1', 'variant_3'].includes(providerBuyboxExpVariant.value ?? '') ? 2 : 1
);
const filterOptionsFirst = computed(() => filterOptions.value.slice(0, myServicesInsertIndex.value));
const filterOptionsRest = computed(() => filterOptions.value.slice(myServicesInsertIndex.value));
// EXP_PROVIDER_BUYBOX_V2

async function handleFilterClick(payload: {
	presentationType: PresentationType | MonetizationType.Free | null;
	additionalContexts?: SnowplowContext[];
}) {
	// EXP_PROVIDER_BUYBOX_V2
	deactivateMyServices();
	// EXP_PROVIDER_BUYBOX_V2
	// updating the buybox is expensive.
	// that's why we split it up in 1) optimistic UI update
	if (!props.isEpisodeBuybox) {
		userStore.setBuyboxFilterTypeOptimistic(payload.presentationType);
	} else {
		emits('episodeFilterChange', payload.presentationType);
	}
	// then 2) give the JS thread time to render the optimistic UI update immediately
	await new Promise(resolve => setTimeout(resolve, 0));
	// and 3) defer the expensive store commit to the end
	if (!props.isEpisodeBuybox) {
		userStore.setBuyboxPresentationType(payload);
	}
}

const _buyboxFilter = ref<undefined | null | PresentationType>(undefined);
const buyboxFilter = computed(() => {
	if (props.isEpisodeBuybox) {
		return props.episodeBuyBoxPresentationType;
	}
	// we need an additional state because buyboxFilterTypeOptimistic's `null` can mean multiple things:
	// - user hasn't filtered anything
	// - user has filtered on "BEST"
	if (_buyboxFilter.value !== undefined) {
		return _buyboxFilter.value;
	}
	return userStore.buyboxFilterTypeOptimistic !== null
		? userStore.buyboxFilterTypeOptimistic
		: userStore.buyboxPresentationType;
});

onMounted(() => {
	// for Apple TV+ search ads
	if (route.query.monetization_types === 'free') {
		return userStore.setBuyboxPresentationType({ presentationType: MonetizationType.Free });
	}

	if (!props.defaultPresentationType) return;

	userStore.setBuyboxPresentationType({
		presentationType: props.defaultPresentationType,
		additionalContexts: props.additionalContexts,
	});
});

// EXP_PROVIDER_BUYBOX_V2
async function handleMyServicesClick() {
	const wasActive = myServicesActive.value;
	await handleMyServicesChipClick('grid', wasActive);
	if (!wasActive) {
		userStore.setBuyboxFilterTypeOptimistic(null);
		userStore.setBuyboxPresentationType({ presentationType: null });
	}
}
// EXP_PROVIDER_BUYBOX_V2

return { __sfc: true,props, emits, languageStore, userStore, route, tc, filterOptions, openCountrySwitcherModal, providerBuyboxExpVariant, deactivateMyServices, myServicesChip, myServicesActive, handleMyServicesChipClick, myServicesInsertIndex, filterOptionsFirst, filterOptionsRest, handleFilterClick, _buyboxFilter, buyboxFilter, handleMyServicesClick, ChipButton, ChipButtons, faAngleDown, faFilter, MyServicesChip, HiddenHorizontalScrollbar }
}

})